
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface CreditPlan {
  id: string;
  name: string;
  credits: number;
  price_inr: number;
  audience: string;
  is_popular: boolean;
  features: string[];
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

export const useCredits = () => {
  const { user, isAuthenticated } = useAuth();
  const [balance, setBalance] = useState(0);
  const [plans, setPlans] = useState<CreditPlan[]>([]);
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchBalance = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('user_credits')
      .select('balance')
      .eq('user_id', user.id)
      .maybeSingle();
    setBalance(data?.balance ?? 0);
  };

  const fetchPlans = async () => {
    const { data } = await supabase
      .from('credit_plans')
      .select('*')
      .order('credits', { ascending: true });
    setPlans((data as CreditPlan[]) ?? []);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchPlans();
    if (isAuthenticated) fetchBalance();
  }, [isAuthenticated, user]);

  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const purchaseCredits = async (planId: string) => {
    if (!isAuthenticated) {
      toast.error("Please sign in to purchase credits");
      return;
    }

    setLoadingPlanId(planId);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("Failed to load payment gateway");

      const { data, error } = await supabase.functions.invoke("create-razorpay-order", {
        body: { planId },
      });
      if (error) throw new Error(error.message);

      const options = {
        key: data.keyId,
        amount: data.amount,
        currency: data.currency,
        name: "ConceptLabs",
        description: `${data.planName} - ${data.credits} Credits`,
        order_id: data.orderId,
        handler: async (response: any) => {
          try {
            const { data: verifyData, error: verifyError } = await supabase.functions.invoke("verify-razorpay-payment", {
              body: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                planId,
              },
            });
            if (verifyError) throw new Error(verifyError.message);
            toast.success(verifyData.message);
            // Reload page so updated credit balance is visible everywhere
            setTimeout(() => window.location.reload(), 1000);
          } catch (err: any) {
            toast.error("Payment verification failed: " + err.message);
          }
        },
        prefill: { email: user?.email },
        theme: { color: "#F59E0B" },
        modal: {
          ondismiss: () => setLoadingPlanId(null),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err: any) {
      toast.error(err.message || "Payment failed");
    } finally {
      setLoadingPlanId(null);
    }
  };

  return {
    balance,
    plans,
    isLoading,
    loadingPlanId,
    purchaseCredits,
    refreshBalance: fetchBalance,
  };
};
